import { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { exportFile } from '../../../features/fileExport/ExcelFile';
import { formatYearList, getBusTypeFromVin, resolveMeasurementStatus } from '../VehicleModelHelper';
import { TOTAL_DISTANCE_IDENTIFIER, TOTAL_FUEL_IDENTIFIER, ENGINE_HOURS_IDENTIFIER, getStatusConfig } from '../constant';

const HERMES_DEVICE_MODELS = ['SI201', 'VT101', 'VT102', 'RE400', 'VT202'];

const useVehicleExport = ({
  vehicleCompatibility,
  dataPoolIdentifiers,
  t
}) => {
  const csvLinkRef = useRef(null);
  const [csvData, setCsvData] = useState([]);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const isIOSMobile = () => {
    return ([
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.userAgentData?.platform || navigator.platform)
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)) && isMobile;
  };

  const transformDataForExport = (vehicleCompatibility, isCsv) => {
    const tableData = [];

    vehicleCompatibility.forEach((vehicle) => {
      const validGroupModels = vehicle.groupModel.filter(groupModel => !!groupModel.vins.length);

      validGroupModels.forEach((groupModel) => {
        groupModel.vins.forEach((busType) => {
          // Calculate isCanCliqRequired logic similar to ClassicTable.js
          let isCanCliqRequired = (groupModel.extras?.connectionPoint || []).includes("CANClick");

          // Create device model mappings from supportedKit
          const deviceModelMap = {};
          HERMES_DEVICE_MODELS.forEach(deviceModel => {
            const supportedDevice = (groupModel.supportedKit || [])
              .filter(kit => kit.deviceModel === deviceModel);

            let sapCodes = "-";

            if (supportedDevice.length > 0) {
              const codes = supportedDevice.map(d => d?.sapCode).join('\n');
              sapCodes = isCsv ? formatForCsv(codes) : codes;
            }

            deviceModelMap[deviceModel] = sapCodes;
          });

          tableData.push({
            make: vehicle.make,
            model: vehicle.model,
            pidVerificationStatus: vehicle.pidVerificationStatus,
            year: formatForCsv(formatYearList(vehicle.yearList)),
            source: groupModel.displaySource,
            isSupported: groupModel.isSupported,
            id: vehicle.id,
            verified: groupModel.verified,
            busType: getBusTypeFromVin(busType.vinDetail.vin),
            dbcFileKey: groupModel.dbcFileKey,
            isCanCliqRequired: isCanCliqRequired,
            deviceModels: deviceModelMap,
            pids: [...busType.modelPids]
          });
        });
      });
    });

    return tableData;
  };

  const getMatrixParsedData = (filteredData, isCsv) => {
    const statusConfig = getStatusConfig();
    const matrixData = transformDataForExport(filteredData, isCsv);
    
    // Get and sort measurements with priority keys first
    const allMeasurements = {};
    Object.values(dataPoolIdentifiers || {}).forEach(group => 
      Object.assign(allMeasurements, group)
    );
    
    const priorityKeys = [TOTAL_DISTANCE_IDENTIFIER, TOTAL_FUEL_IDENTIFIER, ENGINE_HOURS_IDENTIFIER];
    const sortedMeasurements = [
      ...priorityKeys.filter(key => allMeasurements[key]).map(key => [key, allMeasurements[key]]),
      ...Object.entries(allMeasurements).filter(([key]) => !priorityKeys.includes(key))
    ];

    const suffixMap = {
      [TOTAL_DISTANCE_IDENTIFIER]: (calculated) => ` - ${calculated ? 'CANElapsed' : 'CANActual'}`,
      [ENGINE_HOURS_IDENTIFIER]: (calculated) => ` - ${calculated ? 'CANElapsed' : 'CANActual'}`,
      [TOTAL_FUEL_IDENTIFIER]: (calculated) => ` (${calculated ? 'Calculated' : 'Actual'})`
    };

    return matrixData.map((row) => {
      const rowObj = {
        [t('VehicleModel.Make')]: row.make,
        [t('VehicleModel.Model')]: row.model,
        [t('VehicleModel.Year')]: row.year,
        [t('VehicleModel.Source')]: row.source,
        [t('VehicleModel.Supported')]: row.isSupported ? t("VehicleModel.Yes") : (row.isSupported === undefined ? t("VehicleModel.Unknown") : t("VehicleModel.No")),
        [t('VehicleModel.CustomNote.CanCliqRequired')]: row.isCanCliqRequired ? t("VehicleModel.Yes") : t("VehicleModel.No"),
        [t('VehicleModel.DbcKey')]: row.dbcFileKey,
        // Add device model columns
        ...Object.fromEntries(Object.entries(row.deviceModels))
      };

      // Add measurement availability columns
      sortedMeasurements.forEach(([key, value]) => {
        const { status, calculated } = resolveMeasurementStatus({ key, value }, row.pids, row.busType, statusConfig, row);
        const isSupported = status === statusConfig.VERIFIED || status === statusConfig.SUPPORTED;
        
        let columnValue = "-";
        if (isSupported) {
          columnValue = t("VehicleModel.Yes");
        }
        else if (status === statusConfig.NOT_SUPPORTED) {
          columnValue = t("VehicleModel.No");
        }
        else if (status === statusConfig.UNKNOWN) {
          columnValue = t("VehicleModel.Unknown");
        }

        if (isSupported && suffixMap[key]) {
          columnValue += suffixMap[key](calculated);
        }

        rowObj[value] = columnValue;
      });

      return rowObj;
    });
  };

  const handleExportExcel = () => {
    if (isIOSMobile() && (vehicleCompatibility || []).length >= 10000) {
      notification.error({
        placement: 'topRight',
        description: t("ValidationErrors.DataSizeTooLargeError", { number: 10000 }),
        style: { background: "#fff2f0" }
      });
      return;
    }

    let parsedData = getMatrixParsedData(vehicleCompatibility);

    exportFile(parsedData, {
      width: Array.apply(null, { length: Object.keys(parsedData[0]).length }).map(_ => ({ wch: 25 })),
      title: t('VehicleModel.ExportFileName')
    }, t("Common.ExportWarning"));
  };

  const handleExportCSV = () => {
    let parsedData = getMatrixParsedData(vehicleCompatibility, true);

    setCsvData(parsedData);
  };

  useEffect(() => {
    csvData?.length && csvLinkRef?.current?.link?.click();
  }, [csvData]);

  const formatForCsv = (value = '') => {
    let formattedValue = (value ?? '')+"";
    if (value && value.match && value.match(/,|"/)) {
      formattedValue = value.replaceAll('"', '""');
    }
    if (formattedValue.includes(',') || formattedValue.includes('\n')) {
      formattedValue = `"${formattedValue}"`;
    }
    return formattedValue.trim();
  };

  return {
    csvLinkRef,
    csvData,
    handleExportExcel,
    handleExportCSV
  };
};

export default useVehicleExport;